import { throttle, isUndefined } from 'lodash'
import {
  pick,
  isNil,
  pipe,
  evolve,
  map,
  values,
  find,
  propEq,
  pathOr,
  filter,
  allPass
} from 'ramda'
import moment from 'moment'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { NavLink, useHistory } from 'react-router-dom'
import {
  Stack,
  Text as DSText,
  Box,
  OverflowMenu,
  MenuItemLink,
  ButtonLink,
  MenuItem,
  Skeleton
} from '@machobear/design-system'
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  StyledTableCell,
  SingleTableRow,
  Accordion,
  AccordionContent,
  AccordionTableRow,
  AccordionHeader,
  TableHeaderText,
  MobileRowWrapper,
  MobileUnitText,
  MobileUnitDetails,
  CellText
} from '@/components/listings-table'
import PageTitle from '@/components/page-title'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints, structure } from '@/styles'
import { callOr } from '@/utils'
import formatDate from '@/utils/format-date'
import { prefixUnitNumber, formatListingPrice } from '@/utils/listings'
import { TenantsIcon } from '@/components/icons'
import useEventListener from '@/hooks/use-event-listener'
import { LoadingCircle } from '@/components/loading-circle'
import { NoTenantsComponent, TenantsEmptyListComponent } from '@/components/table-list'
import { show as showEndTenancyModal } from '@/features/EndTenancy/end-tenancy-modal'
import formatName from '@/utils/format-name'
import MyTenantsHeader from './my-tenants-header'

const Wrapper = styled.div`
  display: grid;
  place-items: center;
  padding: 0 50px;
  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 0;
  }
`

const TableWrapper = styled.div`
  max-width: 1130px;
`

const LinkCell = ({ children, listing, justifyContent, groupedListing, ...props }) => {
  const url = `/tenant/${listing.listing_id}`
  return (
    <StyledTableCell justifyContent={justifyContent} blueBorder={groupedListing} {...props}>
      <NavLink to={url}>{children}</NavLink>
    </StyledTableCell>
  )
}

const pastStates = ['history']
const currentStates = ['rented']
const upcomingStates = ['upcoming']
const ignoredContractStates = ['sent', 'draft']
const extensionContractStates = ['signed', 'upcoming', 'active']
const isValidContract = (listingState, contractState) => {
  const isValidContractState = !ignoredContractStates.includes(contractState)
  const isNotPreviousContract = !(listingState === 'rented' && contractState === 'history')
  return isValidContractState && isNotPreviousContract
}

const getContractStatus = (allowedStates = [], contract) => {
  if (contract?.contract_type === 'extension') {
    if (contract?.listing_state_machine === 'history') {
      return (
        allowedStates.includes('history') &&
        isValidContract(contract.listing_state_machine, contract.state_machine)
      )
    }
    const startDate = new Date(contract.start_date)
    const currentDate = new Date()
    const tab = startDate > currentDate ? 'upcoming' : 'rented'
    return (
      extensionContractStates.includes(contract.state_machine) &&
      allowedStates.includes(tab) &&
      isValidContract(contract.listing_state_machine, contract.state_machine)
    )
  }
  return (
    allowedStates.includes(contract.listing_state_machine) &&
    isValidContract(contract.listing_state_machine, contract.state_machine)
  )
}

const OptionsMenu = ({ listing, currentTab, isMobile = false, history, data, dispatch }) => {
  const { t } = useTranslation()
  const listingId = listing.listing_id

  const handleRenewTenancy = async () => {
    const buildingId = Number(listing.building_id)
    if (!listing.listing_contract_id) {
      return
    }
    const nextContract = pipe(
      find(propEq('building_id', buildingId)),
      pathOr([], ['contracts']),
      filter(allPass([propEq('listing_id', listingId), propEq('contract_type', 'extension')])),
      pathOr({}, [0])
    )(data)

    if (nextContract.state_machine === 'draft') {
      history.push(`/listing/${listingId}/contract/create/${nextContract.listing_contract_id}`)
      return
    }
    if (nextContract && nextContract.state_machine === 'sent') {
      history.push(`/listing/${listingId}/contract/${nextContract.listing_contract_id}/sign`)
      return
    }
    if (nextContract && nextContract.state_machine === 'signed') {
      history.push(`/tenant/${listingId}/contract/${nextContract.listing_contract_id}`)
      return
    }

    const contract = await dispatch.contract.load(listing.listing_contract_id)
    const lastEndDate = contract?.end_date
    const start_date = lastEndDate ? moment(lastEndDate).add(1, 'days').format('YYYY-MM-DD') : null
    const end_date = start_date ? moment(start_date).add(1, 'years').format('YYYY-MM-DD') : null
    const { body, error } = await dispatch.contract.renewContract({
      contractId: listing?.listing_contract_id,
      ...contract,
      start_date,
      end_date
    })

    if (!error) {
      history.push({
        pathname: `/listing/${listingId}/contract/create/${body.id}`
      })
    }
  }
  return (
    <Box>
      <OverflowMenu label="Options" id="options">
        {currentTab !== 'past' && (
          <MenuItem
            onClick={() => {
              showEndTenancyModal({ listingId })
            }}
          >
            {t('l.tenants.dropdown_menu.end_lease.text', 'End Lease')}
          </MenuItem>
        )}

        <MenuItem onClick={handleRenewTenancy}>
          {t('l.tenants.dropdown_menu.renew_tenancy.text', 'Renew Tenancy')}
        </MenuItem>
        {isMobile && (
          <MenuItemLink
            target="_blank"
            href={`/tenant/${listing.listing_id}/chat?filter=tenants&listing_id=${listing.listing_id}`}
          >
            {t('common.chat', 'Chat')}
          </MenuItemLink>
        )}
      </OverflowMenu>
    </Box>
  )
}

const LoadingState = () => {
  return (
    <Box display="flex" justifyContent="center" padding="xlarge">
      <LoadingCircle />
    </Box>
  )
}

const MyTenants = props => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const [data, setData] = useState()
  const [currentTab, setCurrentTab] = useState('current')
  const [tabCount, setTabCount] = useState({ past: 0, current: 0, upcoming: 0 })
  const [filteredData, setFilteredData] = useState([])
  const [currentLoadedLength, setCurrentLoadedLength] = useState(0)
  const [tabFilteredData, setTabFilteredData] = useState([])
  const [rowsLoading, setRowsLoading] = useState(false)
  const [currentSearchString, setCurrentSearchString] = useState('')
  const [currentSort, setCurrentSort] = useState({
    prop: 'none',
    direction: 'none'
  })

  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  useEffect(() => {
    if (!rowsLoading) return
    setCurrentLoadedLength(currentLoadedLength + 20)
  }, [rowsLoading, currentLoadedLength])

  const onScroll = useMemo(
    () =>
      throttle(async event => {
        const el = event
        const scrollTop = el.target.children[0].scrollTop
        const scrollHeight = el.target.children[0].scrollHeight - el.target.children[0].clientHeight
        const ratio = scrollTop / scrollHeight
        setRowsLoading(ratio > 0.9 && currentLoadedLength < tabFilteredData.length)
      }, 300),
    [currentLoadedLength, tabFilteredData.length]
  )
  useEventListener('scroll', onScroll)

  useEffect(() => {
    if (!!data) return
    const loadContracts = async () => {
      const { body } = await dispatch.contract.getGroupedTenants()
      setData(body)
      setFilteredData(body)
    }
    loadContracts()
  }, [dispatch.contract, data])

  // Calculate count for each tab
  useEffect(() => {
    const calculateTabCounts = filtered => {
      let past = 0
      let current = 0
      let upcoming = 0
      filtered.forEach(building => {
        building.contracts.forEach(contract => {
          if (getContractStatus(pastStates, contract)) {
            past++
          } else if (getContractStatus(currentStates, contract)) {
            current++
          } else if (getContractStatus(upcomingStates, contract)) {
            upcoming++
          }
        })
      })
      setTabCount({ past, current, upcoming })
    }
    calculateTabCounts(filteredData)
  }, [filteredData])

  const noTenants = !data || data?.length === 0

  // Handle tab changes
  const handleSetTab = useCallback(
    machineStates => {
      const filtered = filteredData
        .map(building => ({
          ...building,
          contracts: building.contracts.filter(contract =>
            getContractStatus(machineStates, contract)
          )
        }))
        .filter(building => building.contracts && building.contracts.length > 0)
        .sort((a, b) => {
          if (a.address > b.address) {
            return 1
          } else if (a.address < b.address) {
            return -1
          }
          return 0
        })
      setTabFilteredData(filtered)
      if (filtered.length > 0) setCurrentLoadedLength(20)
      else setCurrentLoadedLength(0)
    },
    [filteredData]
  )
  useEffect(() => {
    if (currentTab === 'past') {
      handleSetTab(pastStates)
    } else if (currentTab === 'current') {
      handleSetTab(currentStates)
    } else if (currentTab === 'upcoming') {
      handleSetTab(upcomingStates)
    }
  }, [currentTab, handleSetTab])

  const onSearch = searchString => {
    setCurrentSearchString(searchString)

    if (searchString?.length > 0 && searchString?.length < 3) {
      return
    }

    if (typeof window !== 'undefined') window.scrollTo(0, 0)

    // TODO debounce
    const filtered = data
      .map(building => ({
        ...building,
        contracts: building.contracts.filter(contract => {
          const searchFields = values(
            pipe(
              pick(['tenants', 'unit_number', 'address', 'building_name', 'city', 'state', 'zip']),
              evolve({
                tenants: callOr(
                  pipe(values, map(pick(['first_name', 'last_name', 'preferred_name']))),
                  null
                )
              })
            )(contract)
          )
          return JSON.stringify(searchFields)
            .toLowerCase()
            .includes(searchString.trim().toLowerCase())
        })
      }))
      .filter(building => building.contracts && building.contracts.length > 0)
    setFilteredData(filtered)
  }

  const getBuildingTitle = (building, count) => {
    const { building_name, address, city, state } = building
    return (
      <Stack space="medium">
        <DSText weight="strong" size="standard">
          {building_name || address} ({count} units)
        </DSText>
        <DSText size="standard">{`${address}, ${city}${
          state === null ? '' : `, ${state}`
        }`}</DSText>
      </Stack>
    )
  }

  const getListingAddress = listing => {
    const { unit_number, address, city, state } = listing
    const unitNumber = unit_number ? `${prefixUnitNumber(unit_number)} - ` : ''
    const fullAddress = `${address}, ${city}, ${state}`
    return (
      <>
        <DSText size="standard">
          {unitNumber}
          {fullAddress}
        </DSText>
      </>
    )
  }

  const getUnitNumber = listing => {
    const { unit_number, unit_type_txt_id } = listing
    if (unit_number) return <DSText size="standard">{prefixUnitNumber(unit_number)}</DSText>
    if (unit_type_txt_id === 'house')
      return <DSText size="standard">{t('common.house', 'House')}</DSText>
    if (unit_type_txt_id === 'townhouse')
      return <DSText size="standard">{t('common.townhouse', 'Townhouse')}</DSText>
    return '-'
  }

  const formatNames = tenants => {
    let tenantFullNames = []
    Object.values(tenants).map(tenant => {
      if (tenant.first_name !== '') {
        tenantFullNames.push(formatName(tenant))
      }
    })
    return tenantFullNames
  }

  const MobileListingRow = ({ listing, displayGaps, groupedListing, singleListing, dispatch }) => {
    return (
      <>
        <LinkCell colSpan="8" width="85%" listing={listing} gapLeft={displayGaps}>
          <MobileRowWrapper indent={groupedListing}>
            <MobileUnitText>
              {singleListing ? getListingAddress(listing) : getUnitNumber(listing)}
            </MobileUnitText>
            <MobileUnitDetails>
              <p>
                {isNil(listing.tenant_first_names) || listing.tenant_first_names[0] === ''
                  ? 'N/A'
                  : formatNames(listing.tenants).join(', ')}
              </p>
              <p>
                {formatDate(listing.start_date, 'MMM D, YYYY') ||
                  t('b.components.view.not_avaiable.text', 'N/A')}{' '}
                -{' '}
                {formatDate(listing.end_date, 'MMM Do, YYYY') ||
                  t('b.components.view.not_avaiable.text', 'N/A')}
              </p>
            </MobileUnitDetails>
          </MobileRowWrapper>
        </LinkCell>
        <LinkCell colSpan="1" width="15%" listing={listing}>
          <MobileUnitText link>
            <OptionsMenu
              listing={listing}
              listingId={listing.listing_id}
              currentTab={currentTab}
              isMobile
              history={history}
              data={data}
              dispatch={dispatch}
            />
          </MobileUnitText>
        </LinkCell>
      </>
    )
  }

  const ListingRow = ({ listing, listingId, displayGaps, groupedListing, singleListing }) => {
    return (
      <>
        <LinkCell
          listing={listing}
          groupedListing={groupedListing}
          justifyContent="flex-start"
          gapLeft={displayGaps}
        >
          <CellText firstCell indent={groupedListing}>
            {singleListing ? getListingAddress(listing) : getUnitNumber(listing)}
          </CellText>
        </LinkCell>
        <LinkCell justifyContent="flex-start" listing={listing}>
          <CellText textAlign="left">
            <Stack align="left" space="small">
              {isNil(listing.tenant_first_names) || listing.tenant_first_names[0] === '' ? (
                <DSText size="standard">{t('b.components.view.not_avaiable.text', 'N/A')}</DSText>
              ) : (
                formatNames(listing.tenants).map(tenantName => (
                  <DSText size="standard" key={`${tenantName}`}>
                    {tenantName}
                  </DSText>
                ))
              )}
            </Stack>
          </CellText>
        </LinkCell>
        <LinkCell justifyContent="flex-start" listing={listing}>
          <CellText>
            <DSText>{formatListingPrice(listing.price)}</DSText>
          </CellText>
        </LinkCell>
        <LinkCell justifyContent="flex-start" listing={listing}>
          <CellText textAlign="left">
            <DSText>
              {formatDate(listing.start_date, 'MMM D, YYYY') ||
                t('b.components.view.not_avaiable.text', 'N/A')}
            </DSText>
          </CellText>
        </LinkCell>
        <LinkCell justifyContent="flex-start" listing={listing}>
          <CellText textAlign="left">
            <DSText>
              {formatDate(listing.end_date, 'MMM Do, YYYY') ||
                t('b.components.view.not_avaiable.text', 'N/A')}
            </DSText>
          </CellText>
        </LinkCell>
        <StyledTableCell>
          <CellText>
            <ButtonLink
              href={`/tenant/${listing.listing_id}/chat?filter=tenants&listing_id=${listing.listing_id}`}
              variant="solid"
              state="default"
              size="small"
              label="Boost Listing"
              iconPosition="leading"
              id={`tenants-table-chat-click-${listing.listing_id}`}
            >
              {t('common.chat', 'Chat')}
            </ButtonLink>
          </CellText>
        </StyledTableCell>
        <StyledTableCell>
          <ButtonLink
            href={`/tenant/${listing.listing_id}`}
            variant="ghost"
            state="default"
            tone="default"
            size="small"
            label="View"
            id={`tenants-table-view-click-${listing.listing_id}`}
          >
            {t('l.listing.list.view.text', 'View')}
          </ButtonLink>
        </StyledTableCell>
        <StyledTableCell>
          <Box
            style={{
              display: 'grid',
              placeItems: 'center',
              height: 35,
              paddingRight: 5
            }}
          >
            <OptionsMenu
              listing={listing}
              listingId={listingId}
              currentTab={currentTab}
              history={history}
              data={data}
              dispatch={dispatch}
            />
          </Box>
        </StyledTableCell>
      </>
    )
  }

  //sort listings within a building
  const orderListings = building => {
    if (currentSort.prop === 'price') {
      currentSort.direction === 'asc'
        ? building.contracts.sort((a, b) => Number(b.price) - Number(a.price))
        : building.contracts.sort((a, b) => Number(a.price) - Number(b.price))
    } else if (currentSort.prop === 'unit') {
      building.contracts.sort((a, b) => {
        if (a.unit_number > b.unit_number) {
          return currentSort.direction === 'asc' ? 1 : -1
        } else if (a.unit_number < b.unit_number) {
          return currentSort.direction === 'asc' ? -1 : 1
        }
        return 0
      })
    } else if (currentSort.prop === 'lease_start') {
      currentSort.direction === 'asc'
        ? building.contracts.sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
        : building.contracts.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
    } else if (currentSort.prop === 'lease_end') {
      currentSort.direction === 'asc'
        ? building.contracts.sort((a, b) => new Date(b.end_date) - new Date(a.end_date))
        : building.contracts.sort((a, b) => new Date(a.end_date) - new Date(b.end_date))
    } else {
      building.contracts.sort((a, b) => {
        if (isNil(a.tenant_first_names)) a.tenant_first_names = ['']
        if (isNil(b.tenant_first_names)) b.tenant_first_names = ['']
        if (a.tenant_first_names[0] > b.tenant_first_names[0]) {
          return currentSort.direction === 'asc' ? 1 : -1
        } else if (a.tenant_first_names[0] < b.tenant_first_names[0]) {
          return currentSort.direction === 'asc' ? -1 : 1
        }
        return 0
      })
    }
    return building
  }

  const getSortedData = () => {
    let sortedData = tabFilteredData
    if (currentSort.prop === 'price') {
      sortedData = tabFilteredData
        .map(building => orderListings(building))
        .sort((a, b) => {
          return currentSort.direction === 'asc'
            ? Number(b.contracts[0].price) - Number(a.contracts[0].price)
            : Number(a.contracts[0].price) - Number(b.contracts[0].price)
        })
    } else if (currentSort.prop === 'unit') {
      sortedData = tabFilteredData
        .map(building => orderListings(building))
        .sort((a, b) => {
          if (a.contracts[0].address > b.contracts[0].address) {
            return currentSort.direction === 'asc' ? 1 : -1
          } else if (a.contracts[0].address < b.contracts[0].address) {
            return currentSort.direction === 'asc' ? -1 : 1
          }
          return 0
        })
    } else if (currentSort.prop === 'lease_start') {
      sortedData = tabFilteredData
        .map(building => orderListings(building))
        .sort((a, b) => {
          return currentSort.direction === 'asc'
            ? new Date(b.contracts[0].start_date) - new Date(a.contracts[0].start_date)
            : new Date(a.contracts[0].start_date) - new Date(b.contracts[0].start_date)
        })
    } else if (currentSort.prop === 'lease_end') {
      sortedData = tabFilteredData
        .map(building => orderListings(building))
        .sort((a, b) => {
          return currentSort.direction === 'asc'
            ? new Date(b.contracts[0].end_date) - new Date(a.contracts[0].end_date)
            : new Date(a.contracts[0].end_date) - new Date(b.contracts[0].end_date)
        })
    } else if (currentSort.prop === 'tenant') {
      sortedData = tabFilteredData
        .map(building => orderListings(building))
        .sort((a, b) => {
          if (isNil(a.contracts[0].tenant_first_names)) a.contracts[0].tenant_first_names = ['']
          if (isNil(b.contracts[0].tenant_first_names)) b.contracts[0].tenant_first_names = ['']
          if (a.contracts[0].tenant_first_names[0] > b.contracts[0].tenant_first_names[0]) {
            return currentSort.direction === 'asc' ? 1 : -1
          } else if (a.contracts[0].tenant_first_names[0] < b.contracts[0].tenant_first_names[0]) {
            return currentSort.direction === 'asc' ? -1 : 1
          }
          return 0
        })
    }
    return sortedData
  }

  const updateCurrentSort = propName => {
    if (currentSort.direction === 'asc' && currentSort.prop === propName) {
      setCurrentSort({
        prop: propName,
        direction: 'desc'
      })
    } else if (currentSort.direction === 'desc' && currentSort.prop === propName) {
      setCurrentSort({
        prop: 'none',
        direction: 'none'
      })
    } else {
      setCurrentSort({
        prop: propName,
        direction: 'asc'
      })
    }
  }

  const overallSortedData = useMemo(
    () => getSortedData(),
    [currentSort.direction, currentSort.prop, tabFilteredData]
  )

  if (isUndefined(data)) {
    return (
      <Wrapper>
        <Helmet>
          <title>{t('l.tenants.list.rented.title', 'Rented')}</title>
        </Helmet>
        <PageTitle>
          <TenantsIcon /> {t('l.tenants.list.rented.title', 'Rented')}
        </PageTitle>
        <Box style={{ pointerEvents: 'none', opacity: 0.8 }}>
          <MyTenantsHeader tabCount={{ past: 0, current: 0, upcoming: 0 }} />
          <TableWrapper>
            <Table>
              {!isMobile && (
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell width="25%">
                      <TableHeaderText firstCell alignLeft>
                        {t('column_header.unit_address', 'Unit Address')}
                      </TableHeaderText>
                    </TableHeaderCell>
                    <TableHeaderCell width="19%">
                      <TableHeaderText firstCell alignLeft>
                        {t('l.tenants.view.tenant.text', 'Tenant')}
                      </TableHeaderText>
                    </TableHeaderCell>
                    <TableHeaderCell width="18%">
                      <TableHeaderText firstCell alignLeft>
                        {t('l.tenants.view.lease_start.text', 'Lease Start')}
                      </TableHeaderText>
                    </TableHeaderCell>
                    <TableHeaderCell width="18%">
                      <TableHeaderText firstCell alignLeft>
                        {t('l.tenants.view.lease_end.text', 'Lease End')}
                      </TableHeaderText>
                    </TableHeaderCell>
                    <TableHeaderCell width="20%">
                      <TableHeaderText>&nbsp;</TableHeaderText>
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
              )}
              <TableBody>
                {[1, 2, 3].map(i => (
                  <TableRow key={i}>
                    {['25%', '19%', '18%', '18%', '20%'].map((width, i) => (
                      <TableHeaderCell key={i} width={width} style={{ height: 48, padding: 12 }}>
                        <Skeleton.Shape height={11} width={'100%'} />
                      </TableHeaderCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        </Box>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Helmet>
        <title>{t('l.tenants.list.rented.title', 'Rented')}</title>
      </Helmet>
      <PageTitle>
        <TenantsIcon /> {t('l.tenants.list.rented.title', 'Rented')}
      </PageTitle>
      <TableWrapper>
        <MyTenantsHeader
          tabCount={tabCount}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          searchString={currentSearchString}
          onSearch={onSearch}
        />
        <Table>
          {!isMobile && (
            <TableHeader>
              <TableRow
                sticky
                top={
                  displayTablet && !isMobile
                    ? structure.header.height + 100
                    : structure.header.height + 158
                }
              >
                <TableHeaderCell
                  onClick={() => {
                    updateCurrentSort('unit')
                  }}
                  active={currentSort.prop === 'unit'}
                  ascending={currentSort.direction === 'asc'}
                  width="25%"
                >
                  <TableHeaderText firstCell alignLeft>
                    {t('column_header.unit_address', 'Unit Address')}
                  </TableHeaderText>
                </TableHeaderCell>
                <TableHeaderCell
                  onClick={() => {
                    updateCurrentSort('tenant')
                  }}
                  active={currentSort.prop === 'tenant'}
                  ascending={currentSort.direction === 'asc'}
                  width="19%"
                >
                  <TableHeaderText alignLeft>
                    {t('l.tenants.view.tenant.text', 'Tenant')}
                  </TableHeaderText>
                </TableHeaderCell>
                <TableHeaderCell
                  onClick={() => {
                    updateCurrentSort('price')
                  }}
                  active={currentSort.prop === 'price'}
                  ascending={currentSort.direction === 'asc'}
                  width="15%"
                >
                  <TableHeaderText alignLeft>{t('common.price', 'Price')}</TableHeaderText>
                </TableHeaderCell>
                <TableHeaderCell
                  onClick={() => {
                    updateCurrentSort('lease_start')
                  }}
                  active={currentSort.prop === 'lease_start'}
                  ascending={currentSort.direction === 'asc'}
                  width="18%"
                >
                  <TableHeaderText alignLeft>
                    {t('l.tenants.view.lease_start.text', 'Lease Start')}
                  </TableHeaderText>
                </TableHeaderCell>
                <TableHeaderCell
                  onClick={() => {
                    updateCurrentSort('lease_end')
                  }}
                  active={currentSort.prop === 'lease_end'}
                  ascending={currentSort.direction === 'asc'}
                  width="18%"
                >
                  <TableHeaderText alignLeft>
                    {t('l.tenants.view.lease_end.text', 'Lease End')}
                  </TableHeaderText>
                </TableHeaderCell>
                <TableHeaderCell width="90px">
                  <TableHeaderText>&nbsp;</TableHeaderText>
                </TableHeaderCell>
                <TableHeaderCell width="66px">
                  <TableHeaderText>&nbsp;</TableHeaderText>
                </TableHeaderCell>
                <TableHeaderCell width="56px">
                  <TableHeaderText>&nbsp;</TableHeaderText>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
          )}
          {!noTenants ? (
            <TableBody>
              {currentLoadedLength > 0 ? (
                overallSortedData.slice(0, currentLoadedLength).map((building, index) => {
                  const contractCount = building.contracts.length

                  if (contractCount === 1) {
                    const contract = building.contracts[0]
                    return (
                      <SingleTableRow key={`${building.listing_contract_id}-${index}`}>
                        {isMobile ? (
                          <MobileListingRow listing={contract} singleListing />
                        ) : (
                          <ListingRow listing={contract} singleListing />
                        )}
                      </SingleTableRow>
                    )
                  }

                  return (
                    <Accordion key={`${building.building_id}-${index}`} expanded={true}>
                      <AccordionHeader
                        colSpan={9}
                        unitCount={building.contracts.length}
                        isMobile={isMobile}
                      >
                        {getBuildingTitle(building, contractCount)}
                      </AccordionHeader>
                      <AccordionContent>
                        {building.contracts.map((contract, i) => {
                          const isLastRow = index + 1 === contractCount

                          return (
                            <AccordionTableRow key={`${contract.listing_contract_id}-${i}`}>
                              {isMobile ? (
                                <MobileListingRow
                                  listing={contract}
                                  displayGaps={!isLastRow}
                                  groupedListing
                                />
                              ) : (
                                <ListingRow
                                  listing={contract}
                                  displayGaps={!isLastRow}
                                  groupedListing
                                />
                              )}
                            </AccordionTableRow>
                          )
                        })}
                      </AccordionContent>
                    </Accordion>
                  )
                })
              ) : currentSearchString.length > 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={8}>
                    <NoTenantsComponent />
                  </StyledTableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={8}>
                    <TenantsEmptyListComponent />
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <StyledTableCell colSpan={8}>
                  <TenantsEmptyListComponent />
                </StyledTableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableWrapper>
      {rowsLoading && <LoadingState />}
    </Wrapper>
  )
}

export default MyTenants
